import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/StarOutline';
import StarFilledIcon from '@material-ui/icons/Star';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useTheme } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityNamespaceFilter,
  EntityTypeFilter,
  useEntityList,
  useStarredEntities,
  EntityUserFilter,
} from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { usePersonalFilterStyles } from './styles';

const whiteIconStyle = { color: 'inherit' } as const;

function toQueryArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function isSameSelection(current: string[], next: string[]): boolean {
  if (current.length !== next.length) return false;
  const currentSorted = [...current].sort();
  const nextSorted = [...next].sort();
  return currentSorted.every((value, index) => value === nextSorted[index]);
}

export const StarredFilter = () => {
  const classes = usePersonalFilterStyles();
  const { filters, updateFilters, backendEntities } = useEntityList();

  const { starredEntities } = useStarredEntities();

  const userFilterValue = (filters.user?.value as string) || '';
  const isStarred = userFilterValue === 'starred';

  const starredCount = useMemo(() => {
    if (starredEntities.size === 0) return 0;
    const starredRefs = Array.from(starredEntities);
    const starredFilter = EntityUserFilter.starred(starredRefs);
    return backendEntities.filter(entity => starredFilter.filterEntity(entity))
      .length;
  }, [backendEntities, starredEntities]);

  const handleToggle = () => {
    if (isStarred) {
      updateFilters({ user: undefined });
    } else {
      const starredRefs = Array.from(starredEntities);
      updateFilters({ user: EntityUserFilter.starred(starredRefs) });
    }
  };

  return (
    <Box className={classes.container}>
      <Tooltip title="Your starred entities">
        <Box className={classes.filterItem}>
          <Checkbox
            checked={isStarred}
            onChange={handleToggle}
            className={classes.checkbox}
            size="small"
            color="primary"
            disabled={starredCount === 0}
          />
          <Box className={classes.contentContainer}>
            <Box className={classes.labelRow}>
              <StarIcon className={classes.icon} />
              <span className={classes.label}>Starred</span>
            </Box>
          </Box>
          <Box className={classes.countBadge}>{starredCount}</Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

export const StarredChip = () => {
  const { filters, updateFilters, backendEntities } = useEntityList();
  const { starredEntities } = useStarredEntities();

  const userFilterValue = (filters.user?.value as string) || '';
  const isStarred = userFilterValue === 'starred';

  const starredCount = useMemo(() => {
    if (starredEntities.size === 0) return 0;
    const starredRefs = Array.from(starredEntities);
    const starredFilter = EntityUserFilter.starred(starredRefs);
    return backendEntities.filter(entity => starredFilter.filterEntity(entity))
      .length;
  }, [backendEntities, starredEntities]);

  const handleToggle = () => {
    if (isStarred) {
      updateFilters({ user: undefined });
    } else {
      const starredRefs = Array.from(starredEntities);
      updateFilters({ user: EntityUserFilter.starred(starredRefs) });
    }
  };

  return (
    <Tooltip title="Filter to starred entities">
      <Chip
        size="small"
        icon={isStarred ? <StarFilledIcon /> : <StarIcon />}
        label={`Starred (${starredCount})`}
        onClick={handleToggle}
        variant={isStarred ? 'default' : 'outlined'}
        color={isStarred ? 'primary' : 'default'}
        disabled={starredCount === 0}
      />
    </Tooltip>
  );
};

export const TypeChip = () => {
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { filters, updateFilters } = useEntityList();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);

  const selectedTypes = useMemo(
    () => filters.type?.getTypes() ?? [],
    [filters.type],
  );

  const kind = filters.kind?.value;
  const prevKindRef = useRef(kind);

  useEffect(() => {
    if (prevKindRef.current !== kind) {
      prevKindRef.current = kind;
      updateFilters({ type: undefined });
    }
    if (!kind) {
      setAvailableTypes([]);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntityFacets({
        filter: { kind },
        facets: ['spec.type'],
      })
      .then(response => {
        if (cancelled) return;
        const types = (response.facets['spec.type'] || []).map(f =>
          f.value.toLocaleLowerCase('en-US'),
        );
        setAvailableTypes([...new Set(types)]);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, catalogApi]);

  const isDisabled = availableTypes.length <= 1;

  const handleToggleType = (type: string) => {
    const newTypes = selectedTypes.includes(type)
      ? selectedTypes.filter(t => t !== type)
      : [...selectedTypes, type];
    updateFilters({
      type: newTypes.length ? new EntityTypeFilter(newTypes) : undefined,
    });
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDisabled) setAnchorEl(e.currentTarget);
  };

  let label = 'Type';
  if (selectedTypes.length === 1) {
    label = selectedTypes[0];
  } else if (selectedTypes.length > 1) {
    label = `${selectedTypes.length} types`;
  }

  return (
    <>
      <Chip
        size="small"
        label={label}
        deleteIcon={
          <ArrowDropDownIcon
            style={selectedTypes.length > 0 ? whiteIconStyle : undefined}
          />
        }
        onDelete={handleOpen}
        onClick={handleOpen}
        variant={selectedTypes.length > 0 ? 'default' : 'outlined'}
        color={selectedTypes.length > 0 ? 'primary' : 'default'}
        disabled={isDisabled}
        style={
          selectedTypes.length > 0
            ? {
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
              }
            : undefined
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableTypes.map(type => (
          <MenuItem key={type} dense onClick={() => handleToggleType(type)}>
            <Checkbox
              checked={selectedTypes.includes(type)}
              size="small"
              color="primary"
              style={{ padding: 4, marginRight: 8 }}
            />
            {type}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

class EntityProjectFilter {
  readonly values: string[];
  constructor(values: string[]) {
    this.values = values;
  }
  getCatalogFilters() {
    return {
      'metadata.annotations.openchoreo.io/project': this.values,
    };
  }
  filterEntity(entity: Entity): boolean {
    const project = entity.metadata.annotations?.['openchoreo.io/project'];
    return project !== undefined && this.values.includes(project);
  }
  toQueryValue() {
    return this.values;
  }
}

export const ProjectChip = () => {
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { filters, updateFilters, queryParameters } = useEntityList();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableProjects, setAvailableProjects] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

  const kind = filters.kind?.value;
  const kindLower = kind?.toLowerCase();
  const prevKindRef = useRef(kind);
  const queryProjects = useMemo(() => {
    const params = queryParameters as Record<
      string,
      string | string[] | undefined
    >;
    return Array.from(
      new Set([
        ...toQueryArray(params.project),
        ...toQueryArray(params['metadata.annotations.openchoreo.io/project']),
      ]),
    );
  }, [queryParameters]);
  // Starts empty (not seeded with the initial query) so the effect below can
  // detect and adopt a query param that is present on first render.
  const prevQueryProjectsRef = useRef<string[]>([]);

  useEffect(() => {
    if (prevKindRef.current !== kind) {
      prevKindRef.current = kind;
      setSelectedProjects([]);
      updateFilters({ project: undefined } as any);
    }
    if (
      kindLower !== 'component' &&
      kindLower !== 'api' &&
      kindLower !== 'resource'
    ) {
      setAvailableProjects([]);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntityFacets({
        filter: { kind: kindLower },
        facets: ['metadata.annotations.openchoreo.io/project'],
      })
      .then(response => {
        if (cancelled) return;
        const projects = (
          response.facets['metadata.annotations.openchoreo.io/project'] || []
        ).map(f => f.value);
        setAvailableProjects([...new Set(projects)].sort());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, catalogApi]);

  useEffect(() => {
    if (
      kindLower !== 'component' &&
      kindLower !== 'api' &&
      kindLower !== 'resource'
    ) {
      return;
    }
    const prevQueryProjects = prevQueryProjectsRef.current;
    prevQueryProjectsRef.current = queryProjects;
    // Only react when the project query param itself changes; otherwise the
    // stale URL param would keep overriding the user's own filter changes.
    if (isSameSelection(prevQueryProjects, queryProjects)) {
      return;
    }
    if (queryProjects.length === 0) {
      if (selectedProjects.length === 0) {
        return;
      }
      setSelectedProjects([]);
      updateFilters({ project: undefined } as any);
      return;
    }
    if (isSameSelection(selectedProjects, queryProjects)) {
      return;
    }
    setSelectedProjects(queryProjects);
    updateFilters({
      project: new EntityProjectFilter(queryProjects),
    } as any);
  }, [kindLower, queryProjects, selectedProjects, updateFilters]);

  if (
    kindLower !== 'component' &&
    kindLower !== 'api' &&
    kindLower !== 'resource'
  ) {
    return null;
  }

  const isDisabled = availableProjects.length <= 1;

  const handleToggleProject = (project: string) => {
    const newProjects = selectedProjects.includes(project)
      ? selectedProjects.filter(p => p !== project)
      : [...selectedProjects, project];
    setSelectedProjects(newProjects);
    updateFilters({
      project: newProjects.length
        ? new EntityProjectFilter(newProjects)
        : undefined,
    } as any);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDisabled) setAnchorEl(e.currentTarget);
  };

  let label = 'Project';
  if (selectedProjects.length === 1) {
    label = selectedProjects[0];
  } else if (selectedProjects.length > 1) {
    label = `${selectedProjects.length} projects`;
  }

  return (
    <>
      <Chip
        size="small"
        label={label}
        deleteIcon={
          <ArrowDropDownIcon
            style={selectedProjects.length > 0 ? whiteIconStyle : undefined}
          />
        }
        onDelete={handleOpen}
        onClick={handleOpen}
        variant={selectedProjects.length > 0 ? 'default' : 'outlined'}
        color={selectedProjects.length > 0 ? 'primary' : 'default'}
        disabled={isDisabled}
        style={
          selectedProjects.length > 0
            ? {
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
              }
            : undefined
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableProjects.map(project => (
          <MenuItem
            key={project}
            dense
            onClick={() => handleToggleProject(project)}
          >
            <Checkbox
              checked={selectedProjects.includes(project)}
              size="small"
              color="primary"
              style={{ padding: 4, marginRight: 8 }}
            />
            {project}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

class EntityComponentFilter {
  readonly values: string[];
  constructor(values: string[]) {
    this.values = values;
  }
  getCatalogFilters() {
    return {
      'metadata.annotations.openchoreo.io/component': this.values,
    };
  }
  filterEntity(entity: Entity): boolean {
    const component = entity.metadata.annotations?.['openchoreo.io/component'];
    return component !== undefined && this.values.includes(component);
  }
  toQueryValue() {
    return this.values;
  }
}

export const ComponentChip = () => {
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { filters, updateFilters, queryParameters } = useEntityList();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableComponents, setAvailableComponents] = useState<string[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<string[]>([]);

  const kind = filters.kind?.value;
  const prevKindRef = useRef(kind);
  const queryComponents = useMemo(() => {
    const params = queryParameters as Record<
      string,
      string | string[] | undefined
    >;
    return Array.from(
      new Set([
        ...toQueryArray(params.component),
        ...toQueryArray(params['metadata.annotations.openchoreo.io/component']),
      ]),
    );
  }, [queryParameters]);
  // Starts empty (not seeded with the initial query) so the effect below can
  // detect and adopt a query param that is present on first render.
  const prevQueryComponentsRef = useRef<string[]>([]);

  useEffect(() => {
    if (prevKindRef.current !== kind) {
      prevKindRef.current = kind;
      setSelectedComponents([]);
      updateFilters({ component: undefined } as any);
    }
    if (kind?.toLowerCase() !== 'api') {
      setAvailableComponents([]);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntityFacets({
        filter: { kind: 'api' },
        facets: ['metadata.annotations.openchoreo.io/component'],
      })
      .then(response => {
        if (cancelled) return;
        const components = (
          response.facets['metadata.annotations.openchoreo.io/component'] || []
        ).map(f => f.value);
        setAvailableComponents([...new Set(components)].sort());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, catalogApi]);

  useEffect(() => {
    if (kind?.toLowerCase() !== 'api') {
      return;
    }
    const prevQueryComponents = prevQueryComponentsRef.current;
    prevQueryComponentsRef.current = queryComponents;
    // Only react when the component query param itself changes; otherwise the
    // stale URL param would keep overriding the user's own filter changes.
    if (isSameSelection(prevQueryComponents, queryComponents)) {
      return;
    }
    if (queryComponents.length === 0) {
      if (selectedComponents.length === 0) {
        return;
      }
      setSelectedComponents([]);
      updateFilters({ component: undefined } as any);
      return;
    }
    if (isSameSelection(selectedComponents, queryComponents)) {
      return;
    }
    setSelectedComponents(queryComponents);
    updateFilters({
      component: new EntityComponentFilter(queryComponents),
    } as any);
  }, [kind, queryComponents, selectedComponents, updateFilters]);

  if (kind?.toLowerCase() !== 'api') {
    return null;
  }

  const isDisabled = availableComponents.length <= 1;

  const handleToggleComponent = (component: string) => {
    const newComponents = selectedComponents.includes(component)
      ? selectedComponents.filter(c => c !== component)
      : [...selectedComponents, component];
    setSelectedComponents(newComponents);
    updateFilters({
      component: newComponents.length
        ? new EntityComponentFilter(newComponents)
        : undefined,
    } as any);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDisabled) setAnchorEl(e.currentTarget);
  };

  let label = 'Component';
  if (selectedComponents.length === 1) {
    label = selectedComponents[0];
  } else if (selectedComponents.length > 1) {
    label = `${selectedComponents.length} components`;
  }

  return (
    <>
      <Chip
        size="small"
        label={label}
        deleteIcon={
          <ArrowDropDownIcon
            style={selectedComponents.length > 0 ? whiteIconStyle : undefined}
          />
        }
        onDelete={handleOpen}
        onClick={handleOpen}
        variant={selectedComponents.length > 0 ? 'default' : 'outlined'}
        color={selectedComponents.length > 0 ? 'primary' : 'default'}
        disabled={isDisabled}
        style={
          selectedComponents.length > 0
            ? {
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
              }
            : undefined
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableComponents.map(component => (
          <MenuItem
            key={component}
            dense
            onClick={() => handleToggleComponent(component)}
          >
            <Checkbox
              checked={selectedComponents.includes(component)}
              size="small"
              color="primary"
              style={{ padding: 4, marginRight: 8 }}
            />
            {component}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export const NamespaceChip = () => {
  const theme = useTheme();
  const catalogApi = useApi(catalogApiRef);
  const { filters, updateFilters, queryParameters } = useEntityList();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableNamespaces, setAvailableNamespaces] = useState<string[]>([]);
  const [selectedNamespaces, setSelectedNamespaces] = useState<string[]>([]);

  const kind = filters.kind?.value;
  const prevKindRef = useRef(kind);
  const queryNamespaces = useMemo(
    () =>
      toQueryArray(
        (queryParameters as Record<string, string | string[] | undefined>)
          .namespace,
      ),
    [queryParameters],
  );
  // Starts empty (not seeded with the initial query) so the effect below can
  // detect and adopt a query param that is present on first render.
  const prevQueryNamespacesRef = useRef<string[]>([]);

  useEffect(() => {
    if (prevKindRef.current !== kind) {
      prevKindRef.current = kind;
      setSelectedNamespaces([]);
      updateFilters({ namespace: undefined });
    }
    if (!kind) {
      setAvailableNamespaces([]);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntityFacets({
        filter: { kind },
        facets: ['metadata.namespace'],
      })
      .then(response => {
        if (cancelled) return;
        const namespaces = (response.facets['metadata.namespace'] || []).map(
          f => f.value,
        );
        setAvailableNamespaces([...new Set(namespaces)].sort());
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, catalogApi]);

  const isDisabled = availableNamespaces.length <= 1;

  useEffect(() => {
    if (!kind) {
      return;
    }
    const prevQueryNamespaces = prevQueryNamespacesRef.current;
    prevQueryNamespacesRef.current = queryNamespaces;
    // Only react when the namespace query param itself changes (e.g. arriving
    // from a header link). The param lingers in the URL after it's adopted, so
    // reacting to any divergence from the current selection would snap the
    // user's own filter changes right back to the stale URL value.
    if (isSameSelection(prevQueryNamespaces, queryNamespaces)) {
      return;
    }
    if (queryNamespaces.length === 0) {
      if (selectedNamespaces.length === 0) {
        return;
      }
      setSelectedNamespaces([]);
      updateFilters({
        namespace: undefined,
      });
      return;
    }
    if (isSameSelection(selectedNamespaces, queryNamespaces)) {
      return;
    }
    setSelectedNamespaces(queryNamespaces);
    updateFilters({
      namespace: new EntityNamespaceFilter(queryNamespaces),
    });
  }, [kind, queryNamespaces, selectedNamespaces, updateFilters]);

  const handleToggleNamespace = (namespace: string) => {
    const newNamespaces = selectedNamespaces.includes(namespace)
      ? selectedNamespaces.filter(n => n !== namespace)
      : [...selectedNamespaces, namespace];
    setSelectedNamespaces(newNamespaces);
    updateFilters({
      namespace: newNamespaces.length
        ? new EntityNamespaceFilter(newNamespaces)
        : undefined,
    });
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDisabled) setAnchorEl(e.currentTarget);
  };

  let label = 'Namespace';
  if (selectedNamespaces.length === 1) {
    label = selectedNamespaces[0];
  } else if (selectedNamespaces.length > 1) {
    label = `${selectedNamespaces.length} namespaces`;
  }

  return (
    <>
      <Chip
        size="small"
        label={label}
        deleteIcon={
          <ArrowDropDownIcon
            style={selectedNamespaces.length > 0 ? whiteIconStyle : undefined}
          />
        }
        onDelete={handleOpen}
        onClick={handleOpen}
        variant={selectedNamespaces.length > 0 ? 'default' : 'outlined'}
        color={selectedNamespaces.length > 0 ? 'primary' : 'default'}
        disabled={isDisabled}
        style={
          selectedNamespaces.length > 0
            ? {
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.primary.main,
              }
            : undefined
        }
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {availableNamespaces.map(namespace => (
          <MenuItem
            key={namespace}
            dense
            onClick={() => handleToggleNamespace(namespace)}
          >
            <Checkbox
              checked={selectedNamespaces.includes(namespace)}
              size="small"
              color="primary"
              style={{ padding: 4, marginRight: 8 }}
            />
            {namespace}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
